import { useCallback, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import type { KundaliRequest } from "@/lib/api/kundali";
import type { CustomerToolKey } from "@ishvaram/domain-data/customer-tools";
import type { PlaceResult } from "@ishvaram/ui/hooks";
import { kundaliFormSchema, toKundaliRequest } from "@ishvaram/api-client/forms/kundali";
import { trackFreeToolStarted } from "@/lib/analytics-free-tools";
import { getBirthDetails, saveBirthDetails } from "@/lib/last-birth-details";
import { birthFormLabels } from "./BirthDetailsForm.i18n";

export interface BirthDetailsFormProps {
  onSubmit: (data: KundaliRequest) => void | Promise<void>;
  loading: boolean;
  submitLabel?: string;
  error?: string;
  heading?: string;
  requireBirthTime?: boolean;
  requireResolvedPlace?: boolean;
  collectName?: boolean;
  optionalName?: boolean;
  language?: "en" | "hi";
  analyticsToolName?: CustomerToolKey;
  analyticsStep?: string;
  analyticsPlacement?: string;
  showTrustStrip?: boolean;
  rememberBirthDetails?: boolean;
}

type BirthFormLabels = ReturnType<typeof birthFormLabels>;

interface BirthFormState {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  selectedPlace: PlaceResult | null;
}

type SubmissionResult =
  | { ok: true; request: KundaliRequest }
  | { ok: false; dateError?: string; formError?: string };

function placeResult(saved: ReturnType<typeof getBirthDetails>): PlaceResult | null {
  if (!saved.birth_place || saved.latitude == null || saved.longitude == null) return null;
  return {
    geoname_id: 0,
    name: saved.birth_place,
    display: saved.birth_place,
    latitude: saved.latitude,
    longitude: saved.longitude,
    timezone: saved.timezone ?? "",
    country: "",
    population: 0,
  };
}

function initialState(rememberBirthDetails: boolean): BirthFormState {
  const saved = rememberBirthDetails ? getBirthDetails() : {};
  return {
    name: saved.name ?? "",
    birthDate: saved.birth_date ?? "",
    birthTime: saved.birth_time ?? "",
    birthPlace: saved.birth_place ?? "",
    selectedPlace: placeResult(saved),
  };
}

function validateDate(value: string, labels: BirthFormLabels): string | undefined {
  if (!value) return undefined;
  const year = Number.parseInt(value.slice(0, 4), 10);
  if (Number.isNaN(year) || year < 1900) return labels.yearTooEarly;
  if (year > new Date().getFullYear()) return labels.futureDate;
  return undefined;
}

function effectiveName(
  state: BirthFormState,
  collectName: boolean,
  optionalName: boolean,
  heading?: string
): string {
  if (!collectName) return heading ?? "Birth details";
  return state.name.trim() || (optionalName ? (heading ?? "") : state.name);
}

function parseSubmission(
  state: BirthFormState,
  labels: BirthFormLabels,
  options: Pick<
    BirthDetailsFormProps,
    "collectName" | "optionalName" | "heading" | "requireResolvedPlace"
  >
): SubmissionResult {
  const dateError = validateDate(state.birthDate, labels);
  if (dateError) return { ok: false, dateError };
  if (options.requireResolvedPlace && !state.selectedPlace) {
    return { ok: false, formError: labels.pickCityToContinue };
  }
  const parsed = kundaliFormSchema.safeParse({
    name: effectiveName(
      state,
      options.collectName ?? true,
      options.optionalName ?? false,
      options.heading
    ),
    birthDate: state.birthDate,
    birthTime: state.birthTime || null,
    birthPlace: state.birthPlace,
    ...(state.selectedPlace && {
      latitude: state.selectedPlace.latitude,
      longitude: state.selectedPlace.longitude,
      timezone: state.selectedPlace.timezone,
    }),
  });
  if (parsed.success) {
    return { ok: true, request: toKundaliRequest(parsed.data) as KundaliRequest };
  }
  const placeFailed = parsed.error.issues.some((issue) => issue.path[0] === "birthPlace");
  return { ok: false, formError: placeFailed ? labels.pickCityToContinue : labels.checkDetails };
}

function rememberSubmission(state: BirthFormState, collectName: boolean): void {
  saveBirthDetails({
    name: collectName ? state.name.trim() : undefined,
    birth_date: state.birthDate,
    birth_time: state.birthTime || undefined,
    birth_place: state.birthPlace,
    ...(state.selectedPlace && {
      latitude: state.selectedPlace.latitude,
      longitude: state.selectedPlace.longitude,
      timezone: state.selectedPlace.timezone,
    }),
  });
}

export function useBirthDetailsFormController(props: BirthDetailsFormProps) {
  const language = props.language ?? "en";
  const labels = birthFormLabels(language);
  const collectName = props.collectName ?? true;
  const optionalName = props.optionalName ?? false;
  const rememberBirthDetails = props.rememberBirthDetails ?? false;
  const [state, setState] = useState(() => initialState(rememberBirthDetails));
  const [dateError, setDateError] = useState<string>();
  const [submitBlockedError, setSubmitBlockedError] = useState<string>();
  const trackedStartRef = useRef(false);
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const trackStart = useCallback(() => {
    if (!props.analyticsToolName || trackedStartRef.current) return;
    trackedStartRef.current = true;
    trackFreeToolStarted({
      tool_name: props.analyticsToolName,
      source_path: window.location.pathname,
      language,
      placement: props.analyticsPlacement ?? "birth_details_form",
      step: props.analyticsStep,
    });
  }, [language, props.analyticsPlacement, props.analyticsStep, props.analyticsToolName]);

  function setField<K extends keyof BirthFormState>(field: K, value: BirthFormState[K]): void {
    setState((current) => ({ ...current, [field]: value }));
  }

  function handleDateChange(value: string): void {
    trackStart();
    setField("birthDate", value);
    setDateError(validateDate(value, labels));
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    trackStart();
    setSubmitBlockedError(undefined);
    const result = parseSubmission(state, labels, props);
    if (!result.ok) {
      setDateError(result.dateError);
      setSubmitBlockedError(result.formError);
      return;
    }
    if (rememberBirthDetails) rememberSubmission(state, collectName);
    void props.onSubmit(result.request);
  }

  return {
    ...state,
    labels,
    collectName,
    optionalName,
    requireBirthTime: props.requireBirthTime ?? false,
    showTrustStrip: props.showTrustStrip ?? false,
    effectiveSubmitLabel: props.submitLabel ?? labels.defaultSubmit,
    dateError,
    submitBlockedError,
    todayIso,
    placeIsUnresolved: state.birthPlace.trim().length > 0 && state.selectedPlace === null,
    trackStart,
    setField,
    handleDateChange,
    handleSubmit,
  };
}

export type { BirthFormLabels };
